
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UserCircle } from 'lucide-react';

interface AuthButtonProps {
  className?: string;
}

const AuthButton = ({ className }: AuthButtonProps = {}) => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();

  // Check for profile loading state
  const profileLoading = user && !profile;

  // Don't render anything while loading
  if (loading || profileLoading) {
    return null;
  }

  // Get user's initials for avatar fallback
  const getInitials = () => {
    if (profile?.username) {
      return profile.username.substring(0, 2).toUpperCase();
    }
    if (profile?.full_name) {
      return profile.full_name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  if (user) {
    // Authenticated state - tap avatar navigates directly to profile
    return (
      <Avatar
        className={className ?? "h-10 w-10 ring-2 ring-gray-200 hover:ring-gray-300 transition-all duration-200 cursor-pointer"}
        onClick={() => navigate(user.id ? `/profile/${user.id}` : '/profile')}
      >
        <AvatarImage src={profile?.avatar_url || ''} alt={profile?.username || 'User Avatar'} />
        <AvatarFallback className="bg-gray-500 text-white font-semibold">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <button
      onClick={() => navigate('/login')}
      className="p-1 rounded-full text-muted-foreground hover:text-foreground transition-colors"
      aria-label="Sign in"
    >
      <UserCircle className="h-5 w-5" />
    </button>
  );
};

export default AuthButton;
